import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import { CommissionOption, RuleCondition, ensureAgentHubBrands } from '~/consts/agentHub';

import { useGetPlaceNamesByIds } from '~/hooks/apis/search/useGetPlaceNamesByIds';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import SearchService from '~/services/SearchService';

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  existingConditions: RuleCondition['value'];
}

/**
 * Location rule condition only (`rules.location`). Flight departure/arrival use
 * {@link AgentHubCommissionsFlightLocationInput}.
 */
function AgentHubCommissionsLocationInput({ setCreatedConditions, existingConditions }: Props) {
  const { tenant } = useCurrentTenant();
  const [loadingState, setLoadingState] = useState<Utils.FetchingState>('idle');
  const [result, setResult] = useState<Array<{ value: string; label: string; description: string }>>([]);
  const [selectedLocations, setSelectedLocations] = useState<CommissionOption>(existingConditions || []);
  const [noOptionsText, setNoOptionsText] = useState<string>('Start typing to search for locations');
  const selectedLocationPlaceIds = useMemo(
    () => selectedLocations.map((location) => String(location.value)).filter(Boolean),
    [selectedLocations],
  );
  const { data: placeNames = {} } = useGetPlaceNamesByIds(selectedLocationPlaceIds);

  const debounceGetLocations = useMemo(
    () =>
      debounce((search: string) => {
        if (!search) {
          setResult([]);
          return;
        }
        setLoadingState('loading');
        SearchService.typeahead(search, {
          type: ['province_state', 'multi_city_vicinity', 'city'],
          brand: ensureAgentHubBrands(tenant.brand),
          region: 'AU',
          limit: 10,
          vertical: 'hotel',
          priority: 'next',
        })
          .then((response) => {
            setResult(
              response.result.map((location) => ({
                value: location.id,
                label: location.format.mainText,
                description: location.format.secondaryText,
              })),
            );
            setLoadingState('success');
          })
          .catch(() => {
            setResult([]);
            setNoOptionsText('Failed to fetch location');
          });
      }, 300),
    [tenant.brand],
  );

  useEffect(() => {
    setCreatedConditions(
      {
        type: 'location',
        value: selectedLocations.map((location) => ({ value: location.value, label: location.label })),
      },
      true,
    );
  }, [selectedLocations, setCreatedConditions]);

  useEffect(() => {
    setSelectedLocations((currentLocations) => {
      let hasChanged = false;
      const nextLocations = currentLocations.map((location) => {
        const placeId = String(location.value);
        const label = placeNames[placeId] ?? location.label;

        if (label === location.label) {
          return location;
        }

        hasChanged = true;
        return { ...location, label };
      });

      return hasChanged ? nextLocations : currentLocations;
    });
  }, [placeNames]);

  const handleInputChange = (_: SyntheticEvent, newInputValue: string) => {
    if (!newInputValue) {
      setNoOptionsText('Start typing to search for locations');
      return;
    }
    debounceGetLocations(newInputValue);
  };

  const handleChange = (_: SyntheticEvent, value: CommissionOption) => {
    setSelectedLocations(value);
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Autocomplete
          multiple
          loading={loadingState === 'loading'}
          options={result}
          getOptionLabel={(option) => option.label}
          renderOption={(props, option) => (
            <Box display="flex" flexDirection="column" width="100%" component="li" {...props}>
              <Typography variant="body2" width="100%">
                {option.label}
              </Typography>
              <Typography variant="caption" color="textSecondary" width="100%">
                {result.find((r) => r.value === option.value)?.description}
              </Typography>
            </Box>
          )}
          defaultValue={[]}
          filterSelectedOptions
          filterOptions={(options) => options}
          renderInput={(params) => (
            <TextField {...params} label="Location" placeholder={selectedLocations.length > 0 ? '' : 'Location'} />
          )}
          onInputChange={handleInputChange}
          value={selectedLocations}
          onChange={handleChange}
          noOptionsText={noOptionsText}
          disablePortal
        />
        <Typography variant="caption" color="textSecondary">
          Start typing to search for locations
        </Typography>
      </Box>
    </Stack>
  );
}

export default AgentHubCommissionsLocationInput;
